// pages/tabs/GeneralDetailsTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Card, Form, Button, Row, Col, Spinner, Alert, Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { fetchWardsThunk } from "../../../../store/slices/wardsSlice";
import { fetchMeterTypesThunk } from "../../../../store/slices/meterTypesSlice";
import { fetchSlrmTypesThunk } from "../../../../store/slices/slrmTypesSlice";
import { updateHouseThunk, clearHouseMasterError } from "../../../../store/slices/houseMasterSlice";
import { fetchHouseProfileThunk } from "../../../../store/slices/houseProfileSlice";

import "../../../../css/master-common.css";

export default function GeneralDetailsTab({  house, activeTab, onRefreshProfile }) {
  const dispatch = useDispatch();

  const wardsState = useSelector((s) => s.wards) || {};
  const meterState = useSelector((s) => s.meterTypes) || {};
  const slrmState = useSelector((s) => s.slrmTypes) || {};
  const hmState = useSelector((s) => s.houseMaster) || {};

  const wards = wardsState.list || [];
  const wardsLoading = !!wardsState.loading;

  const meterTypes = meterState.list || [];
  const meterLoading = !!meterState.loading;

  const slrmTypes = slrmState.list || [];
  const slrmLoading = !!slrmState.loading;

  const saving = !!hmState.saving;
  const error = hmState.error;

  // ✅ permissions
  const perms = useSelector((s) => s.permissions?.map?.house_master) || {};
  const canUpdate = perms.can_update !== false;

  // ✅ only run when THIS tab is active (because tabs are mounted always)
  const isActive = activeTab === "general_details";

  // ✅ input type="date" expects YYYY-MM-DD (supports ISO too)
  const toInputDate = (v) => {
    if (!v) return "";
    const s = String(v).trim();

    // YYYY-MM-DD or ISO
    const m1 = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m1) return `${m1[1]}-${m1[2]}-${m1[3]}`;

    // DD-MM-YYYY or DD/MM/YYYY
    const m2 = s.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})/);
    if (m2) return `${m2[3]}-${m2[2]}-${m2[1]}`;

    return "";
  };

  // ✅ pick meter/slrm ID from either *_id or direct field
  const pickId = (obj, keys = []) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") return String(v);
    }
    return "";
  };

  const [form, setForm] = useState({
    home_id: "",
    door_no: "",
    qr_code: "",
    family_head: "",
    mobile: "",
    ward_id: "",
    mtr_type: "",
    slrm_type: "",
    last_bill_date: "",
    slrm_last_bill_date: "",
    slrm_water_together: "no",
    is_water: false,
    is_slrm: false,
    status: "Active",
  });

  // ✅ load dropdowns ONLY when tab becomes active
  useEffect(() => {
    if (!isActive) return;

    // If already loaded, no need to refetch (optional guard)
    if (!wardsState.list?.length) dispatch(fetchWardsThunk());
    if (!meterState.list?.length) dispatch(fetchMeterTypesThunk());
    if (!slrmState.list?.length) dispatch(fetchSlrmTypesThunk());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, dispatch]);

  // ✅ optional: when tab opens, refetch profile to show latest data
  // useEffect(() => {
  //   if (!isActive) return;
  //   const hid = house?.home_id;
  //   if (hid) dispatch(fetchHouseProfileThunk(hid));
  // }, [isActive, house?.home_id, dispatch]);

  // toast error
  useEffect(() => {
    if (!error) return;
    toast.error(String(error));
    dispatch(clearHouseMasterError());
  }, [error, dispatch]);

  // prefill from profile house
  useEffect(() => {
    if (!house) return;

    const meterTypeId = pickId(house, ["mtr_type_id", "mtr_type"]);
    const slrmTypeId = pickId(house, ["slrm_type_id", "slrm_type"]);

    setForm({
      home_id: house.home_id,
      door_no: house.door_no || "",
      qr_code: house.qr_code || "",
      family_head: house.family_head || "",
      mobile: house.mobile || "",
      ward_id: String(house.ward_id || ""),

      mtr_type: meterTypeId,
      slrm_type: slrmTypeId,

      last_bill_date: toInputDate(house.last_bill_date),
      slrm_last_bill_date: toInputDate(house.slrm_last_bill_date),

      slrm_water_together:
        String(house.slrm_water_together || "").toLowerCase() === "yes" ? "yes" : "no",

      is_water: String(house.is_water || "").toLowerCase() === "yes",
      is_slrm: String(house.is_slrm || "").toLowerCase() === "yes",

      status: house.status || "Active",
    });
  }, [house]);

  const loading = (wardsLoading || meterLoading || slrmLoading) && isActive;

  const homeMeta = useMemo(() => (form.home_id ? String(form.home_id) : null), [form.home_id]);
const onSubmit = async (e) => {
  e.preventDefault();

  if (!canUpdate) return toast.error("No update permission");

  const door_no = String(form.door_no || "").trim();
  const ward_id = String(form.ward_id || "").trim();

  if (!door_no) return toast.warning("Door No required");
  if (!ward_id) return toast.warning("Ward required");

  const payload = {
    home_id: form.home_id,
    qr_code: String(form.qr_code || "").trim(),
    door_no,
    family_head: String(form.family_head || "").trim(),
    mobile: String(form.mobile || "").trim(),
    ward_id,

    mtr_type: form.mtr_type ? String(form.mtr_type) : null,
    slrm_type: form.slrm_type ? String(form.slrm_type) : null,

    is_water: form.is_water ? "yes" : "no",
    is_slrm: form.is_slrm ? "yes" : "no",

    status: form.status || "Active",

    last_bill_date: form.last_bill_date || null,
    slrm_last_bill_date: form.slrm_last_bill_date || null,

    slrm_water_together: form.slrm_water_together || "no",
  };

  const r = await dispatch(updateHouseThunk(payload));

  if (updateHouseThunk.fulfilled.match(r)) {
    toast.success("House updated ✅");

    if (onRefreshProfile) {
      onRefreshProfile();
    } else {
      dispatch(fetchHouseProfileThunk(form.home_id));
    }

  } else {
    const msg =
      r?.payload?.message ||
      r?.payload ||
      r?.error?.message ||
      "Update failed";

    toast.error(String(msg));
  }
};
  // ✅ if tab not active, render nothing OR render lightweight
  // (optional; but best for performance)
  if (!isActive) return null;

  return (
    <Row className="hm-form-page">
      <Col lg={12}>
        <Card className="hm-form-card">
          <Card.Header className="hm-form-header">
            <div className="hm-form-title-wrap">
              <div>
                <h5 className="mb-1 hm-form-title">General Details</h5>
                <div className="hm-form-subtitle">
                  Update door number, ward, meter/slrm type, bill dates, status and connections.
                </div>
              </div>

              {homeMeta ? (
                <Badge bg="light" text="dark" className="hm-form-badge">
                  Home ID: <span className="hm-form-badge-val">{homeMeta}</span>
                </Badge>
              ) : null}
            </div>
          </Card.Header>

          <Card.Body className="hm-form-body">
            {loading ? (
              <div className="hm-form-loading">
                <Spinner animation="border" />
                <div className="mt-2 text-muted">Loading master data…</div>
              </div>
            ) : (
              <Form onSubmit={onSubmit} className="hm-form">
                <Row className="g-3">
                  {/* Door No */}
                  <Col md={6}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">
                        Door No <span className="hm-req">*</span>
                      </Form.Label>
                      <Form.Control
                        className="hm-control"
                        required
                        value={form.door_no}
                        placeholder="e.g. 12/A"
                        onChange={(e) => setForm((p) => ({ ...p, door_no: e.target.value }))}
                        disabled={!canUpdate}
                      />
                    </Form.Group>
                  </Col>

                  {/* QR Code */}
                  <Col md={6}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">QR Code</Form.Label>
                      <Form.Control
                        className="hm-control"
                        value={form.qr_code}
                        placeholder="Optional QR code"
                        onChange={(e) => setForm((p) => ({ ...p, qr_code: e.target.value }))}
                        disabled={!canUpdate}
                      />
                    </Form.Group>
                  </Col>

                  {/* Family Head */}
                  <Col md={6}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">
                        Family Head <span className="hm-req">*</span>
                      </Form.Label>
                      <Form.Control
                        className="hm-control"
                        required
                        value={form.family_head}
                        placeholder="e.g. Ramesh Kumar"
                        onChange={(e) => setForm((p) => ({ ...p, family_head: e.target.value }))}
                        disabled={!canUpdate}
                      />
                    </Form.Group>
                  </Col>

                  {/* Mobile */}
                  <Col md={6}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">Mobile</Form.Label>
                      <Form.Control
                        className="hm-control"
                        value={form.mobile}
                        placeholder="10-digit mobile number"
                        onChange={(e) => setForm((p) => ({ ...p, mobile: e.target.value }))}
                        disabled={!canUpdate}
                      />
                      <div className="hm-help">Optional. Used for SMS / communication.</div>
                    </Form.Group>
                  </Col>

                  {/* Ward */}
                  <Col md={6}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">
                        Ward <span className="hm-req">*</span>
                      </Form.Label>
                      <Form.Select
                        className="hm-control"
                        required
                        value={form.ward_id}
                        onChange={(e) => setForm((p) => ({ ...p, ward_id: e.target.value }))}
                        disabled={!canUpdate}
                      >
                        <option value="">Select ward</option>
                        {wards.map((w) => (
                          <option key={String(w.ward_id)} value={String(w.ward_id)}>
                            {w.ward_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* Meter Type */}
                  <Col md={6}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">
                        Water Rate<span className="hm-req">*</span>
                      </Form.Label>
                      <Form.Select
                        className="hm-control"
                        required
                        value={form.mtr_type}
                        onChange={(e) => setForm((p) => ({ ...p, mtr_type: e.target.value }))}
                        disabled={!canUpdate}
                      >
                        <option value="">Select Water Rate</option>
                        {meterTypes.map((m) => (
                          <option key={String(m.wbs_id)} value={String(m.wbs_id)}>
                            {m.mtr_type}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* SLRM Type */}
                  <Col md={6}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">Slrm Rate</Form.Label>
                      <Form.Select
                        className="hm-control"
                        value={form.slrm_type}
                        onChange={(e) => setForm((p) => ({ ...p, slrm_type: e.target.value }))}
                        disabled={!canUpdate}
                      >
                        <option value="">Select SLRM type</option>
                        {slrmTypes.map((m) => (
                          <option key={String(m.wbs_id)} value={String(m.wbs_id)}>
                            {m.mtr_type}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* Last Water Bill Date */}
                  <Col md={6}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">Last Water Bill Date</Form.Label>
                      <Form.Control
                        className="hm-control"
                        type="date"
                        value={form.last_bill_date || ""}
                        onChange={(e) => setForm((p) => ({ ...p, last_bill_date: e.target.value }))}
                        disabled={!canUpdate}
                      />
                    </Form.Group>
                  </Col>

                  {/* Last SLRM Bill Date */}
                  <Col md={6}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">Last SLRM Bill Date</Form.Label>
                      <Form.Control
                        className="hm-control"
                        type="date"
                        value={form.slrm_last_bill_date || ""}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, slrm_last_bill_date: e.target.value }))
                        }
                        disabled={!canUpdate}
                      />
                    </Form.Group>
                  </Col>

                  {/* SLRM + Water Together */}
                  <Col md={6}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">Bill Together</Form.Label>
                      <Form.Select
                        className="hm-control"
                        value={form.slrm_water_together}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, slrm_water_together: e.target.value }))
                        }
                        disabled={!canUpdate}
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* Connections */}
                  <Col md={6}>
                    <div className="hm-switch-card">
                      <div className="hm-switch-title">Connections</div>

                      <Form.Check
                        type="switch"
                        id="is_water"
                        className="hm-switch"
                        label="Water Connection"
                        checked={form.is_water}
                        onChange={(e) => setForm((p) => ({ ...p, is_water: e.target.checked }))}
                        disabled={!canUpdate}
                      />

                      <Form.Check
                        type="switch"
                        id="is_slrm"
                        className="hm-switch"
                        label="SLRM Collection"
                        checked={form.is_slrm}
                        onChange={(e) => setForm((p) => ({ ...p, is_slrm: e.target.checked }))}
                        disabled={!canUpdate}
                      />

                      <div className="hm-help mt-2">Turn ON only if connection is active.</div>
                    </div>
                  </Col>

                  {/* Status */}
                  <Col md={12}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">Status</Form.Label>
                      <div className="d-flex gap-3">
                        <Form.Check
                          type="radio"
                          label="Active"
                          name="status"
                          value="Active"
                          checked={form.status === "Active"}
                          onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                          inline
                          disabled={!canUpdate}
                        />
                        <Form.Check
                          type="radio"
                          label="Inactive"
                          name="status"
                          value="Inactive"
                          checked={form.status === "Inactive"}
                          onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                          inline
                          disabled={!canUpdate}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="hm-actions">
                  <div className="hm-actions-left">
                    <span className="hm-help">
                      Fields marked <span className="hm-req">*</span> are required.
                    </span>
                  </div>

                  <div className="hm-actions-right">
                    <Button
                      type="submit"
                      className="hm-primary-btn"
                      disabled={
                        saving ||
                        !canUpdate ||
                        !String(form.door_no || "").trim() ||
                        !String(form.ward_id || "").trim()
                      }
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Updating…
                        </>
                      ) : (
                        "Update"
                      )}
                    </Button>
                  </div>
                </div>

                {!canUpdate ? (
                  <Alert variant="warning" className="mt-3 mb-0">
                    You don’t have permission to update house details.
                  </Alert>
                ) : null}
              </Form>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}